import type { InvitationParams } from "@/types/common";

/**
 * Extracts invitation parameters from URL search params.
 *
 * Expected URL format:
 * /?invitation=1q8YSH5gyBNdYqTzkZcW7zpO1UUpb4XN&organization=org_wTCLYHZSiUhzaAFu&organization_name=deloitte-spa-dev-org
 *
 * Both `invitation` and `organization` are required. Auth0 validates format and validity server-side.
 *
 * @param searchParams - URLSearchParams object (e.g., from window.location.search)
 * @returns InvitationParams if both required params are present and non-empty, null otherwise
 */
export function extractInvitationParams(
  searchParams: URLSearchParams,
): InvitationParams | null {
  const invitation = searchParams.get("invitation");
  const organization = searchParams.get("organization");
  const organizationName = searchParams.get("organization_name");

  // Both invitation and organization are required and must be non-empty
  if (!invitation?.trim() || !organization?.trim()) {
    return null;
  }

  return {
    invitation: invitation.trim(),
    organization: organization.trim(),
    organizationName: organizationName?.trim() || undefined,
  };
}

/**
 * Builds authorization parameters for Auth0 invitation flow.
 * These parameters are passed to loginWithRedirect().
 *
 * @param params - Extracted invitation parameters
 * @returns Auth0 authorizationParams object with invitation and organization
 */
export function buildInvitationAuthParams(
  params: InvitationParams,
): Record<string, unknown> {
  return {
    invitation: params.invitation,
    organization: params.organization,
  };
}

/**
 * Builds a query string that preserves invitation context during internal redirects.
 *
 * @param params - Extracted invitation parameters
 * @returns URL query string without leading '?' (e.g. invitation=...&organization=...)
 */
export function buildInvitationQueryString(params: InvitationParams): string {
  return new URLSearchParams({
    invitation: params.invitation,
    organization: params.organization,
    ...(params.organizationName
      ? { organization_name: params.organizationName }
      : {}),
  }).toString();
}
